/**
 * GuestPicker — reusable customer chooser for the New Reservation drawers.
 *
 * Responsibilities
 * ────────────────
 * - Two modes: "existing" (default) and "new".
 * - Existing: searchable autocomplete against GET /guests?search=…
 *   • min 2 chars, 300 ms debounce, tenant-scoped by the backend.
 *   • On select → notifies parent with `guest_id`, shows summary card,
 *     offers "Change" to reopen the search.
 * - New: name + email (validated) + phone (E.164, PhoneInput) + document
 *   fields. Runs POST /guests/check-duplicate on blur so we surface likely
 *   matches BEFORE the user submits. Duplicates never block the parent
 *   form on their own — the backend re-checks and enforces uniqueness.
 *
 * State contract with the parent
 * ──────────────────────────────
 * The parent owns a single opaque `value` object of shape:
 *   {
 *     mode: "existing" | "new",
 *     guest_id?: string,           // set when mode === "existing"
 *     guest?: object,              // full guest doc for summary rendering
 *     first_name, last_name, email, phone, document_type, document_number,
 *   }
 * `onChange(next)` receives the whole object.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, User, X, UserPlus, UserCheck, AlertTriangle, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";

// Small helper — same rules as backend: trim + lowercase, reject blatantly
// malformed inputs. Real validity is re-checked by the server.
function validateEmail(raw) {
    if (!raw) return { ok: true, value: null };
    const s = String(raw).trim().toLowerCase();
    if (!s) return { ok: true, value: null };
    // RFC-lite pattern; matches everything email-validator would.
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    return { ok, value: s };
}

function useDebounce(value, ms) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return debounced;
}

// Anchored, portal-mounted dropdown so `overflow-hidden` drawers don't clip.
function PortalDropdown({ anchorRef, open, children, testid }) {
    const [pos, setPos] = useState(null);
    useEffect(() => {
        if (!open || !anchorRef.current) return;
        const compute = () => {
            const r = anchorRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
        };
        compute();
        window.addEventListener("scroll", compute, true);
        window.addEventListener("resize", compute);
        return () => {
            window.removeEventListener("scroll", compute, true);
            window.removeEventListener("resize", compute);
        };
    }, [open, anchorRef]);
    if (!open || !pos) return null;
    return createPortal(
        <div
            data-testid={testid}
            style={{ position: "absolute", top: pos.top + 4, left: pos.left, width: pos.width, zIndex: 200 }}
            className="rounded-md border border-border bg-card shadow-xl"
        >{children}</div>,
        document.body,
    );
}

export default function GuestPicker({ value, onChange, testid = "guest-picker", disabled = false }) {
    const { t } = useTranslation();
    const mode = value?.mode || "existing";
    const setMode = (m) => onChange({ ...(value || {}), mode: m });

    // ── Existing-guest search state ──────────────────────────────────────
    const [query, setQuery] = useState("");
    const debounced = useDebounce(query, 300);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (mode !== "existing") return;
        if (!debounced || debounced.trim().length < 2) {
            setResults([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        api.get("/guests", { params: { search: debounced.trim(), limit: 8 } })
            .then(({ data }) => {
                if (!cancelled) {
                    setResults(Array.isArray(data) ? data : []);
                    setOpen(true);
                }
            })
            .catch(() => { if (!cancelled) setResults([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [debounced, mode]);

    const pickGuest = (g) => {
        onChange({
            mode: "existing",
            guest_id: g.id,
            guest: g,
            first_name: g.first_name,
            last_name: g.last_name,
            email: g.email || "",
            phone: g.phone || "",
        });
        setOpen(false);
        setQuery("");
    };

    const clearGuest = () => {
        onChange({ mode: "existing" });
        setQuery("");
        setOpen(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    // ── New-guest state (owned by parent via `value`) ────────────────────
    const setField = (k, v) => onChange({ ...(value || {}), mode: "new", [k]: v });
    const emailCheck = useMemo(() => validateEmail(value?.email), [value?.email]);

    const [emailError, setEmailError] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [duplicateMatches, setDuplicateMatches] = useState([]);
    const [duplicateStrong, setDuplicateStrong] = useState(false);

    // Run backend duplicate check on blur (email or phone) or when both settle.
    const runDupCheck = useCallback(async () => {
        if (mode !== "new") return;
        const payload = {
            email: value?.email || null,
            phone: value?.phone || null,
            document_number: value?.document_number || null,
            first_name: value?.first_name || null,
            last_name: value?.last_name || null,
        };
        if (!payload.email && !payload.phone && !payload.document_number &&
            !(payload.first_name && payload.last_name)) {
            setDuplicateMatches([]);
            setDuplicateStrong(false);
            return;
        }
        try {
            const { data } = await api.post("/guests/check-duplicate", payload);
            setDuplicateMatches(data.matches || []);
            setDuplicateStrong(!!data.strong);
        } catch { /* soft failure — user still submits, backend re-checks */ }
    }, [mode, value?.email, value?.phone, value?.document_number, value?.first_name, value?.last_name]);

    // Email blur/onchange → validate + de-dup
    const onEmailBlur = () => {
        const c = validateEmail(value?.email);
        if (!c.ok) {
            setEmailError(t("guest_picker.invalid_email", "Vendosni një adresë emaili të vlefshme."));
        } else {
            setEmailError("");
            if (c.value) setField("email", c.value);
            runDupCheck();
        }
    };
    const onPhoneChange = (e164) => {
        setField("phone", e164);
        setPhoneError("");
    };
    const onPhoneBlur = () => {
        // If user typed and value stayed empty, treat as invalid.
        if (value?.phoneRaw && !value?.phone) {
            setPhoneError(t("guest_picker.invalid_phone", "Vendosni një numër telefoni të vlefshëm."));
        }
        runDupCheck();
    };

    // Debounced dup-check when name/document change too
    const dupDebounced = useDebounce([value?.email, value?.phone, value?.document_number, value?.first_name, value?.last_name].join("|"), 500);
    useEffect(() => { runDupCheck(); /* eslint-disable-next-line */ }, [dupDebounced]);

    // ── Render ──────────────────────────────────────────────────────────
    // Summary card (existing guest already chosen)
    if (mode === "existing" && value?.guest_id && value?.guest) {
        const g = value.guest;
        return (
            <div className="space-y-2" data-testid={`${testid}-summary`}>
                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 p-2.5 dark:border-emerald-900/40 dark:bg-emerald-950/30">
                    <div className="flex items-center gap-2">
                        <UserCheck size={14} className="text-emerald-700 dark:text-emerald-400" />
                        <div>
                            <div className="text-[13px] font-semibold text-emerald-900 dark:text-emerald-200">
                                {g.first_name} {g.last_name}
                                {g.vip && <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">VIP</span>}
                            </div>
                            <div className="text-[11px] text-emerald-800/80 dark:text-emerald-300/80">
                                {[g.email, g.phone, `ID: ${(g.id || "").slice(0, 8)}`].filter(Boolean).join(" · ")}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={clearGuest}
                        data-testid={`${testid}-change`}
                        disabled={disabled}
                        className="rounded-md border border-emerald-300 bg-white/70 px-2 py-1 text-[11px] font-medium text-emerald-900 hover:bg-white disabled:opacity-50 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100"
                    >{t("guest_picker.change", "Ndrysho klientin")}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2" data-testid={testid}>
            {/* Mode toggle */}
            <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "existing"}
                    onClick={() => setMode("existing")}
                    disabled={disabled}
                    data-testid={`${testid}-tab-existing`}
                    className={`flex items-center gap-1.5 rounded-[5px] px-2.5 py-1 text-[11.5px] font-medium transition ${mode === "existing" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                    <Search size={11} /> {t("guest_picker.pick_existing", "Zgjidh klient ekzistues")}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "new"}
                    onClick={() => setMode("new")}
                    disabled={disabled}
                    data-testid={`${testid}-tab-new`}
                    className={`flex items-center gap-1.5 rounded-[5px] px-2.5 py-1 text-[11.5px] font-medium transition ${mode === "new" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                    <UserPlus size={11} /> {t("guest_picker.create_new", "Krijo klient të ri")}
                </button>
            </div>

            {mode === "existing" && (
                <div className="relative">
                    <div className="relative" ref={inputRef}>
                        <Search size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            className="akm-input pl-7"
                            placeholder={t("guest_picker.search_placeholder", "Kërko sipas emrit, emailit, telefonit ose ID…")}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => query && setOpen(true)}
                            disabled={disabled}
                            data-testid={`${testid}-search-input`}
                        />
                    </div>
                    <PortalDropdown anchorRef={inputRef} open={open && query.trim().length >= 2} testid={`${testid}-dropdown`}>
                        <div className="max-h-72 overflow-y-auto p-1">
                            {loading && <div className="p-3 text-[12px] text-muted-foreground">{t("guest_picker.searching", "Duke kërkuar…")}</div>}
                            {!loading && results.length === 0 && (
                                <div className="p-3 text-[12px] text-muted-foreground">
                                    {t("guest_picker.no_results", "Nuk u gjet asnjë klient.")}
                                    <button
                                        type="button"
                                        className="ml-1 text-primary hover:underline"
                                        onClick={() => { setMode("new"); setOpen(false); }}
                                        data-testid={`${testid}-switch-new`}
                                    >{t("guest_picker.create_new_short", "Krijo të ri")}</button>
                                </div>
                            )}
                            {!loading && results.map((g) => (
                                <button
                                    key={g.id}
                                    type="button"
                                    onClick={() => pickGuest(g)}
                                    data-testid={`${testid}-result-${g.id}`}
                                    className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] hover:bg-muted/60"
                                >
                                    <User size={12} className="mt-0.5 text-muted-foreground" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 font-medium">
                                            <span className="truncate">{g.first_name} {g.last_name}</span>
                                            {g.vip && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">VIP</span>}
                                        </div>
                                        <div className="truncate text-[11px] text-muted-foreground">
                                            {[g.email, g.phone].filter(Boolean).join(" · ") || "—"}
                                        </div>
                                    </div>
                                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                                        {(g.id || "").slice(0, 8)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </PortalDropdown>
                </div>
            )}

            {mode === "new" && (
                <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <div className="text-[11px] font-medium text-muted-foreground mb-1">
                                {t("reservations.first_name", "Emri")}<span className="text-red-600"> *</span>
                            </div>
                            <input
                                className="akm-input"
                                value={value?.first_name || ""}
                                onChange={(e) => setField("first_name", e.target.value)}
                                onBlur={runDupCheck}
                                disabled={disabled}
                                data-testid={`${testid}-first-name`}
                            />
                        </label>
                        <label className="block">
                            <div className="text-[11px] font-medium text-muted-foreground mb-1">
                                {t("reservations.last_name", "Mbiemri")}<span className="text-red-600"> *</span>
                            </div>
                            <input
                                className="akm-input"
                                value={value?.last_name || ""}
                                onChange={(e) => setField("last_name", e.target.value)}
                                onBlur={runDupCheck}
                                disabled={disabled}
                                data-testid={`${testid}-last-name`}
                            />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <div className="text-[11px] font-medium text-muted-foreground mb-1">Email</div>
                            <input
                                className={`akm-input ${emailError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                type="email"
                                value={value?.email || ""}
                                onChange={(e) => { setField("email", e.target.value); setEmailError(""); }}
                                onBlur={onEmailBlur}
                                disabled={disabled}
                                data-testid={`${testid}-email`}
                            />
                            {emailError && (
                                <div className="mt-1 text-[11px] text-red-600" data-testid={`${testid}-email-error`}>{emailError}</div>
                            )}
                        </label>
                        <div>
                            <PhoneInput
                                value={value?.phone || ""}
                                onChange={onPhoneChange}
                                defaultCountry="AL"
                                label={t("common.phone", "Telefon")}
                                testid={`${testid}-phone`}
                            />
                            {phoneError && (
                                <div className="mt-1 text-[11px] text-red-600" data-testid={`${testid}-phone-error`}>{phoneError}</div>
                            )}
                        </div>
                    </div>

                    {duplicateMatches.length > 0 && (
                        <div
                            className="rounded-md border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-800/50 dark:bg-amber-950/30"
                            data-testid={`${testid}-duplicate-warning`}
                        >
                            <div className="flex items-start gap-2">
                                <AlertTriangle size={13} className="mt-0.5 text-amber-700" />
                                <div className="flex-1 space-y-1.5">
                                    <div className="text-[12px] font-medium text-amber-900 dark:text-amber-200">
                                        {duplicateStrong
                                            ? t("guest_picker.dup_strong", "Ky klient duket se ekziston në sistem. Zgjidh profilin ekzistues për të vazhduar.")
                                            : t("guest_picker.dup_weak", "Klientë të ngjashëm janë gjetur. Kontrollo përpara se të krijosh një të ri.")
                                        }
                                    </div>
                                    <div className="space-y-1">
                                        {duplicateMatches.slice(0, 3).map((g) => (
                                            <div key={g.id} className="flex items-center justify-between gap-2 rounded bg-white/70 px-2 py-1 dark:bg-amber-900/30">
                                                <div className="min-w-0 flex-1 truncate text-[11.5px]">
                                                    <span className="font-medium">{g.first_name} {g.last_name}</span>
                                                    <span className="text-muted-foreground"> · {[g.email, g.phone, `ID: ${(g.id || "").slice(0, 8)}`].filter(Boolean).join(" · ")}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => pickGuest(g)}
                                                    data-testid={`${testid}-use-existing-${g.id}`}
                                                    className="shrink-0 rounded border border-amber-400 bg-amber-100 px-2 py-0.5 text-[10.5px] font-medium text-amber-900 hover:bg-amber-200"
                                                >{t("guest_picker.use_existing", "Përdor këtë")}</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Helper: convert the GuestPicker's internal `value` into the payload keys
 * the reservation endpoints expect.
 */
export function toReservationGuestPayload(value) {
    if (!value) return {};
    if (value.mode === "existing" && value.guest_id) {
        return { guest_id: value.guest_id };
    }
    return {
        guest_first_name: (value.first_name || "").trim(),
        guest_last_name: (value.last_name || "").trim(),
        ...(value.email ? { guest_email: String(value.email).trim().toLowerCase() } : {}),
        ...(value.phone ? { guest_phone: value.phone } : {}),
    };
}

/**
 * Helper: for the parent form's validity check (used to disable submit).
 * A new-guest requires first + last name; existing requires guest_id.
 */
export function isGuestPickerValid(value) {
    if (!value) return false;
    if (value.mode === "existing") return !!value.guest_id;
    return !!((value.first_name || "").trim()) && !!((value.last_name || "").trim());
}
